import { Component, OnInit, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { PermissionModel } from '@admin/shared/models/permission-model';
import { SettingsService } from '../shared/services/settings.service';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { PermissionFilterModel } from '../shared/models/admin-permission-filter-model';
import { AdminSubmitDataModel } from '../shared/models/admin-submit-data-model';
import { CustomMessageService } from '@admin/core/message.service';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { FieldState } from '@admin/shared/models/field-state';
import { LookupService } from '@admin/shared/services/lookup.service';
import {
  Validator,
  IValidator,
  ValidatedField,
  MessageLevel,
  IPromise,
} from '@admin/shared/validation/validators';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { Patterns } from '@admin/shared/validation/patterns';
import { FeatureTypeEnum } from '@admin/shared/models/feature-type-enum';
import { RoleEnum } from '@admin/shared/models/role-enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-admin-edit',
  templateUrl: './admin-edit.component.html',
  styleUrls: ['./admin-edit.component.less'],
  standalone: false,
})
export class AdminEditComponent implements OnInit {
  private fieldStates: {
    fullName: FieldState;
    email: FieldState;
    mobileNumber: FieldState;
    phoneNumber: FieldState;
    phoneExtension: FieldState;
    username: FieldState;
    password: FieldState;
  };
  private filterPagingWrapper: PagingFilter<PermissionFilterModel>;

  public permissions: PermissionModel[];
  public adminUser: AdminSubmitDataModel;
  public checkAll: boolean;
  public editMode: boolean;

  public FeatureTypeEnum = FeatureTypeEnum;

  public customRoles: { title: string; role: RoleEnum | null }[];
  public selectedOption: any;

  public roles: number[];

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private translationService: TranslateService,
    private settingsService: SettingsService,
    private messageService: CustomMessageService,
    private lookupService: LookupService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.filterPagingWrapper = new PagingFilter(new PermissionFilterModel());
    this.adminUser = new AdminSubmitDataModel();
    this.checkAll = false;
    this.roles = [];
    this.customRoles = [
      { title: 'Remove existing role', role: null },
      { title: 'Limited', role: RoleEnum.CustomerAssistanceLimited },
      { title: 'Unlimited', role: RoleEnum.CustomerAssistance },
    ];
  }

  ngOnInit() {
    let config = this.lookupService.getCachedLookupData();
    this.route.params.subscribe((p) => {
      let adminId = +p['id'];
      if (adminId) {
        this.editMode = true;
        this.initData(adminId);
        this.initRole(adminId);
        this.initFieldState();
      }
    });
  }

  private initData(id: number) {
    this.settingsService.GetAdmin(id).subscribe((u) => {
      this.adminUser.adminId = u.adminId;
      this.adminUser.fullName = u.fullName;
      this.adminUser.username = u.username;
      this.adminUser.password = u.password;
      this.adminUser.email = u.email;
      this.adminUser.mobileNumber = u.mobileNumber;
      this.adminUser.phoneNumber = u.phoneNumber;
      this.adminUser.phoneExtension = u.phoneExtension;
      this.adminUser.position = u.position;
      u.permissions.forEach((p) => {
        p.features.forEach((f) => {
          this.adminUser.permissions.push(f.featureId);
        });
      });

      this.initPermissions();
    });
  }

  private initPermissions() {
    this.settingsService
      .GetPermissions(this.filterPagingWrapper)
      .subscribe((data) => {
        this.permissions = data.items;
        this.initPermissionCheck();
      });
  }

  private initRole(adminId: number) {
    this.settingsService.GetLimitedOrUnlimitedRole(adminId).subscribe((p) => {
      p.items.splice(0, 1);
      this.roles = p.items.map((item) => item.roleId);

      if (this.roles.includes(RoleEnum.CustomerAssistance))
        this.adminUser.roleId = RoleEnum.CustomerAssistance;
      else if (this.roles.includes(RoleEnum.CustomerAssistanceLimited))
        this.adminUser.roleId = RoleEnum.CustomerAssistanceLimited;
      else this.adminUser.roleId = null;
    });
  }

  private initPermissionCheck() {
    for (let permission of this.permissions) {
      for (let feature of permission.features) {
        if (this.adminUser.permissions.includes(feature.featureId))
          feature.isChecked = true;
      }
    }
  }

  private initFieldState() {
    this.fieldStates = {
      fullName: new FieldState(() => this.adminUser.fullName),
      email: new FieldState(() => this.adminUser.email),
      mobileNumber: new FieldState(() => this.adminUser.mobileNumber),
      phoneNumber: new FieldState(() => this.adminUser.phoneNumber),
      phoneExtension: new FieldState(() => this.adminUser.phoneExtension),
      username: new FieldState(() => this.adminUser.username),
      password: new FieldState(() => this.adminUser.password),
    };
  }

  private invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }
    return result;
  }

  private getValidators() {
    let admin = this.adminUser;
    let field = this.field;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let incorrectPhone = this.translationService.instant(
      'message.PhoneNumberIncorrectFormat'
    );
    let isAlreadyInUse = this.translationService.instant(
      'message.IsAlreadyInUse'
    );

    return [
      Validator.forRequired(field.fullName, () => admin.fullName, emptyMessage),
      Validator.forPattern(
        field.fullName,
        () => admin.fullName,
        Patterns.personName,
        incorrectFormat
      ),

      Validator.forRequired(field.email, () => admin.email, emptyMessage),
      Validator.forPattern(
        field.email,
        () => admin.email,
        Patterns.emailNew,
        incorrectFormat
      ),
      this.editMode
        ? Validator.forAsync(
            field.email,
            () => {
              var p = this.settingsService.ValidateEmail(
                admin.adminId,
                admin.email
              );
              return <IPromise>{
                catch: (error) => p.catch(error),
                then: (data) => p.then(data),
                finally: () => {},
              };
            },
            (response: any) => {
              var isValid = response.isSuccess;
              return {
                message: isValid ? null : isAlreadyInUse,
                level: isValid ? null : MessageLevel.Error,
                isValid: isValid,
              };
            }
          )
        : Validator.forBypass(field.email),

      Validator.forNotRequired(field.mobileNumber, () => admin.mobileNumber),
      Validator.forPattern(
        field.mobileNumber,
        () => admin.mobileNumber,
        Patterns.phoneNumber,
        incorrectFormat
      ),

      Validator.forNotRequired(
        field.phoneExtension,
        () => admin.phoneExtension
      ),
      Validator.forPattern(
        field.phoneExtension,
        () => admin.phoneExtension,
        Patterns.phoneNumberExtension,
        incorrectFormat
      ),

      Validator.forNotRequired(field.phoneNumber, () => admin.phoneNumber),
      Validator.forPattern(
        field.phoneNumber,
        () => admin.phoneNumber,
        Patterns.phoneNumber,
        incorrectPhone
      ),

      Validator.forRequired(field.username, () => admin.username, emptyMessage),
      this.editMode
        ? Validator.forAsync(
            field.username,
            () => {
              var p = this.settingsService.ValidateUsername(
                admin.adminId,
                admin.username
              );
              return <IPromise>{
                catch: (error) => p.catch(error),
                then: (data) => p.then(data),
                finally: () => {},
              };
            },
            (response: any) => {
              var isValid = response.isSuccess;
              return {
                message: isValid ? null : isAlreadyInUse,
                level: isValid ? null : MessageLevel.Error,
                isValid: isValid,
              };
            }
          )
        : Validator.forBypass(field.username),

      Validator.forNotRequired(field.password, () => admin.password),
      Validator.forMinLength(
        field.password,
        () => admin.password,
        8,
        incorrectFormat
      ),
    ];
  }

  private setAdminPermissions() {
    let activeFeatures = this.permissions
      .reduce((a, b) => {
        return a.concat(b.features);
      }, [])
      .filter((c) => c.isChecked)
      .map((c) => c.featureId);

    this.adminUser.permissions = activeFeatures;
  }

  public onCheckAll() {
    for (let permission of this.permissions)
      for (let feature of permission.features)
        feature.isChecked = !this.checkAll;
  }

  public onCancel() {
    this.router.navigate(['./settings/admin-users']);
  }

  public onSave() {
    let newUser = this.adminUser;
    let validators = this.getValidators();

    this.setAdminPermissions();
    this.invokeValidation(validators, () => {
      this.settingsService
        .UpdateAdmin(newUser, newUser.adminId)
        .subscribe(() => {
          this.messageService.pushMessage(<ApplicationMessage>{
            type: MessageType.Success,
            sourceId: ApplicationMessageSource.global,
            summary: 'new user saved',
            detail: this.translationService.instant('message.SaveMessage'),
          });
          this.editMode = false;
          this.router.navigate(['./settings/admin-users']);
        });
    });
  }
}
